import type { Crumb, Field, Member, ObjEntity, PageItemRow, Parent, SpaceOut } from "../../api/types.gen.ts";
import { SLOT, t } from "../../i18n/index.ts";
import { ATTACHMENTS } from "../../lib/attach.ts";
import { carriedValues, type LinkColumns } from "../../lib/backrefs.ts";
import { mailDate, mailText } from "../../lib/emailText.ts";
import { alignOf, figuresOf } from "../../lib/fieldTypes.ts";
import { formatInstant, formatRelative } from "../../lib/format.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { SELECTED_ROW, type Selection } from "../../lib/selection.ts";
import { titleOf } from "../../lib/titles.ts";
import { groupsOf, useBranch } from "../../lib/tree.ts";
import { BookmarkButton } from "../../ui/BookmarkButton.tsx";
import { IconButton, Link } from "../../ui/Button.tsx";
import { Chevron } from "../../ui/Chevron.tsx";
import { ItemCommentCount, RefCount, ThreadCount } from "../../ui/counts.tsx";
import { CloseIcon } from "../../ui/icons.tsx";
import { Slotted } from "../../ui/misc.tsx";
import { SelectAll, SelectBox } from "../../ui/selection.tsx";
import { LINK } from "../../ui/styles.ts";
import { SortTh, Td, Th } from "../../ui/Table.tsx";
import { Breadcrumb } from "../folders/breadcrumb.tsx";
import { type Around, ItemDrift } from "../folders/drift.tsx";
import { dragIds } from "../folders/drop.ts";
import { VisibilityMark } from "../item/visibility.tsx";
import { columnsOf } from "./columns.ts";
import { EntityLabel } from "./entityLabel.tsx";
import { AttachmentCount, PaperclipIcon, PeekKindIcon } from "./icons.tsx";
import { RowIssues, Subtree } from "./tree.tsx";
import { Value } from "./values.tsx";

type Row = PageItemRow["data"][number];

/** "in RES: IMP 806…": the item an attachment came with. */
export function AttachedTo({ space, parent }: { space: SpaceOut; parent: Parent }) {
  return (
    <span class="block text-xs font-normal text-zinc-500">
      <Slotted text={t("items.attachedTo", { title: SLOT })}>
        <Link href={spaceUrl(space, `/items/${parent.id}`)} class="hover:underline">
          {titleOf(parent)}
        </Link>
      </Slotted>
    </span>
  );
}

/** A field's cell; a ref field's count opens its items under the row. */
function FieldCell({
  f,
  row,
  region,
  onOpen,
}: {
  f: Field;
  row: Row;
  region: string | null | undefined;
  onOpen: (key: string) => void;
}) {
  const n = row.refs?.[f.key]?.length ?? 0;
  return (
    <Td align={alignOf(f.type)} class={figuresOf(f.type)}>
      {f.type === "ref" ? (
        n > 0 ? (
          <button
            type="button"
            class="rounded-full focus-visible:outline-2 focus-visible:outline-blue-600"
            title={t("items.tree.group", { field: f.label || f.key, title: titleOf(row) })}
            onClick={() => onOpen(f.key)}
          >
            <RefCount n={n} />
          </button>
        ) : (
          <RefCount n={0} />
        )
      ) : (
        <Value field={f} value={row.data?.[f.key]} region={region} />
      )}
    </Td>
  );
}

/** What a list of items (as rows, or as cards) shows them with. */
export interface ItemListProps {
  space: SpaceOut;
  entity: ObjEntity | undefined;
  entities: readonly ObjEntity[];
  members: readonly Member[] | undefined;
  /** A field left out (a ref field every item has pointing to the same item). */
  hide?: string | undefined;
  /** The values each item's link carries (shown first). */
  link?: LinkColumns | undefined;
  /** Conversations listed one per row: say how many emails each has. */
  conversations?: boolean | undefined;
  /** Takes an item off the list it's on (a field's links). */
  onRemove?: ((id: string) => void) | undefined;
  /** Where the rows are among folders (a list of the space's items). */
  place?: RowPlace | undefined;
  /** Cards can be dragged into folders. */
  draggable?: boolean | undefined;
}

/** Where rows are among folders: the folder they're in (each row's sharing compared with its), or, listed flat from
 *  anywhere, each one's breadcrumb. */
interface RowPlace {
  around?: Around | undefined;
  pathOf?: ((folder: string) => readonly Crumb[]) | undefined;
  /** After a row's sharing was matched. */
  onChange: () => void;
}

type RowProps = ItemListProps & {
  row: Row;
  /** Only emails listed: From, To and Sent columns. */
  emails?: boolean | undefined;
  /** Rows can be selected: a box before each. */
  sel?: Selection | undefined;
};

/** Takes an item off the list it's on (a field's links): its ×. */
export function UnlinkButton({ title, onClick }: { title: string; onClick: () => void }) {
  return (
    <IconButton size="xs" label={t("fields.unlink", { title })} onClick={onClick}>
      <CloseIcon size={14} />
    </IconButton>
  );
}

/**
 * The items table's head: kind, title (and link values), attachments, the entity's fields (or the entity), issues,
 * reviewed, updated. With `onSort`, titles, fields and the date sort.
 */
export function ItemsHead(p: {
  entity: ObjEntity | undefined;
  sort?: string;
  onSort?: (sort: string) => void;
  hide?: string | undefined;
  link?: LinkColumns | undefined;
  /** Only emails listed: who wrote to whom, and when, as columns. */
  emails?: boolean;
  /** Rows can be taken off the list (a field's links): a column for that. */
  removable?: boolean;
  /** Rows can be selected: the head's box selects the page's (`ids`). */
  select?: { sel: Selection; ids: readonly string[] } | undefined;
}) {
  const { sort, onSort } = p;
  const th = (label: string, field: string, align?: "right") =>
    onSort && sort !== undefined ? (
      <SortTh key={field} label={label} field={field} sort={sort} onSort={onSort} {...(align && { align })} />
    ) : (
      <Th key={field} {...(align && { align })}>
        {label}
      </Th>
    );
  return (
    <thead class="bg-zinc-50">
      <tr>
        <Th>
          {p.select ? <SelectAll sel={p.select.sel} ids={p.select.ids} count={true} class="mr-1 align-middle" /> : null}
          <span class="sr-only">{t("items.kind")}</span>
        </Th>
        {th(t("items.title"), "title")}
        {(p.link?.fields ?? []).map((f) => (
          <Th key={`link.${f.key}`} align={alignOf(f.type)}>
            {f.label || f.key}
          </Th>
        ))}
        <Th>
          <PaperclipIcon label={t("items.attachments")} />
        </Th>
        {/* Issues next to the title, before the entity's fields (which can run wide). */}
        <Th>{t("items.issues")}</Th>
        {p.emails ? (
          <>
            {th(t("email.from"), "data.from")}
            <Th>{t("email.to")}</Th>
            {th(t("email.sent"), "data.date")}
          </>
        ) : null}
        {p.entity
          ? columnsOf(p.entity, p.hide).map((f) =>
              f.type === "ref" ? (
                <Th key={f.key} align={alignOf(f.type)}>
                  {f.label || f.key}
                </Th>
              ) : (
                th(f.label || f.key, `data.${f.key}`, alignOf(f.type))
              ),
            )
          : null}
        <Th>{t("items.reviewed")}</Th>
        {th(t("items.updated"), "updated")}
        {p.removable ? (
          <Th>
            <span class="sr-only">{t("common.remove")}</span>
          </Th>
        ) : null}
      </tr>
    </thead>
  );
}

/** An email's sender, recipients and when it was sent, as cells. */
function MailCells({ row }: { row: Row }) {
  const from = mailText(row.data?.["from"]);
  const to = mailText(row.data?.["to"]);
  const date = mailDate(row.data);
  return (
    <>
      <Td class="max-w-56 truncate" title={from}>
        {from}
      </Td>
      <Td class="max-w-56 truncate text-zinc-600" title={to}>
        {to}
      </Td>
      <Td class="whitespace-nowrap" {...(date ? { title: formatInstant(date) } : {})}>
        {date ? formatRelative(date) : "—"}
      </Td>
    </>
  );
}

/** An item's entity beside its title, small (nothing without one). */
function InlineEntity({ entity }: { entity: ObjEntity | undefined }) {
  return entity ? (
    <span class="ml-2 inline-block align-middle">
      <EntityLabel entity={entity} />
    </span>
  ) : null;
}

/** A row of the items table. */
function ItemRow({
  space,
  row,
  entity,
  entities,
  members,
  hide,
  link,
  emails,
  conversations,
  onRemove,
  sel,
  place,
  draggable,
}: RowProps) {
  const carried = link ? carriedValues(row, link) : {};
  const branch = useBranch();
  const hasLinks = groupsOf(row, entities).length > 0;
  return (
    <>
      {/* Dragged into a folder (with the rest of the selection, when it's selected). */}
      <tr
        class={`group${sel?.has(row.id) ? ` ${SELECTED_ROW}` : ""}`}
        draggable={sel !== undefined || draggable}
        onDragStart={
          sel !== undefined || draggable
            ? (e) => (sel?.has(row.id) ? dragIds(e, sel.ids) : dragIds(e, [row.id], row.folder ?? null))
            : undefined
        }
      >
        <Td class="w-px pr-0">
          <span class="flex items-center gap-1">
            {sel ? <SelectBox sel={sel} id={row.id} title={titleOf(row)} class="mr-1" /> : null}
            {hasLinks ? (
              <Chevron
                open={branch.open}
                label={t("items.tree.links", { title: titleOf(row) })}
                onToggle={branch.toggle}
              />
            ) : (
              <span class="size-5 shrink-0" />
            )}
            <PeekKindIcon space={space} item={row} />
          </span>
        </Td>
        <Td>
          {/* Inline, so the count follows the title's last word instead of wrapping on its own. */}
          <span>
            <Link href={spaceUrl(space, `/items/${row.id}`)} class={LINK}>
              {titleOf(row)}
            </Link>
            {/* Private or public, right after its title (the organization's: nothing), in line with its comments. */}
            {row.visibility === "org" ? null : (
              <span class="ml-1.5 inline-flex h-5 items-center align-middle">
                <VisibilityMark item={row} members={members} />
              </span>
            )}
            {/* Seen by more people than its folder: marked beside who sees it. */}
            {place?.around && !place.pathOf ? (
              <span class="ml-1 inline-flex h-5 items-center align-middle">
                <ItemDrift space={space} folder={place.around} item={row} onDone={place.onChange} />
              </span>
            ) : null}
            {conversations ? <ThreadCount n={row.thread_count} /> : null}
            <ItemCommentCount space={space} id={row.id} />
            {/* Bookmarked: marked (and unmarked there); else offered as the row is pointed at. */}
            <BookmarkButton
              space={space}
              target={{ kind: "item", item: row.id }}
              size="2xs"
              quiet={true}
              class="ml-1 align-middle"
            />
            {/* Items of any entity: which one, after the title (as in a group of the tree). */}
            {entity ? null : <InlineEntity entity={entities.find((e) => e.id === row.entity)} />}
          </span>
          {row.attached_to ? <AttachedTo space={space} parent={row.attached_to} /> : null}
          {place?.pathOf && row.folder ? (
            <Breadcrumb space={space} path={place.pathOf(row.folder)} small={true} class="mt-0.5" />
          ) : null}
        </Td>
        {(link?.fields ?? []).map((f) => (
          <Td key={`link.${f.key}`} align={alignOf(f.type)} class={figuresOf(f.type)}>
            <Value field={f} value={carried[f.key]} region={space.region} />
          </Td>
        ))}
        <Td class="w-px">
          <AttachmentCount n={row.refs?.[ATTACHMENTS]?.length ?? 0} />
        </Td>
        <Td>
          <RowIssues row={row} open={branch.open} />
        </Td>
        {emails ? <MailCells row={row} /> : null}
        {entity
          ? columnsOf(entity, hide).map((f) => (
              <FieldCell key={f.key} f={f} row={row} region={space.region} onOpen={branch.openAt} />
            ))
          : null}
        <Td>{row.reviewed_at ? formatRelative(row.reviewed_at) : "—"}</Td>
        <Td title={formatInstant(row.updated_at)}>{formatRelative(row.updated_at)}</Td>
        {onRemove ? (
          <Td class="w-px">
            <UnlinkButton title={titleOf(row)} onClick={() => onRemove(row.id)} />
          </Td>
        ) : null}
      </tr>
      {branch.open ? <Subtree space={space} entities={entities} path={[]} row={row} branch={branch} depth={1} /> : null}
    </>
  );
}

export function Rows({ page, ...p }: ItemListProps & { page: PageItemRow; emails?: boolean; sel?: Selection }) {
  return (
    <tbody class="divide-y divide-zinc-100">
      {page.data.map((row) => (
        <ItemRow key={row.id} {...p} row={row} />
      ))}
    </tbody>
  );
}
